import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@utils/classNames';
import { isBenignBrowserMessagePortError } from '@utils/benignClientErrors';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((type: ToastType, title: string, message?: string) => {
    if (
      type === 'error' &&
      (isBenignBrowserMessagePortError(title) || isBenignBrowserMessagePortError(message))
    ) {
      return;
    }

    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = { id, type, title, message };
    
    setToasts(prev => [...prev, newToast]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0" />;
      case 'error': return <XCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />;
      case 'warning': return <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0" />;
      case 'info': return <Info className="w-4 h-4 md:w-5 md:h-5 text-blue-400 flex-shrink-0" />;
    }
  };

  const getColors = (type: ToastType) => {
    switch (type) {
      case 'success': return 'bg-slate-900 border-l-4 border-primary';
      case 'error': return 'bg-slate-900 border-l-4 border-red-500';
      case 'warning': return 'bg-slate-900 border-l-4 border-amber-500';
      case 'info': return 'bg-slate-900 border-l-4 border-blue-500';
    }
  };

  const success = useCallback((title: string, message?: string) => showToast('success', title, message), [showToast]);
  const error = useCallback((title: string, message?: string) => showToast('error', title, message), [showToast]);
  const info = useCallback((title: string, message?: string) => showToast('info', title, message), [showToast]);
  const warning = useCallback((title: string, message?: string) => showToast('warning', title, message), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-2 left-2 right-2 sm:top-3 sm:right-3 sm:left-auto md:top-4 md:right-4 z-[9999] space-y-2 pointer-events-none max-w-full sm:max-w-[calc(100vw-1.5rem)]">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto w-full sm:min-w-[280px] md:min-w-[320px] sm:max-w-md p-3 sm:p-4 rounded-lg border border-white/10 shadow-2xl animate-slide-in-right',
              getColors(toast.type)
            )}
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(toast.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-inter font-bold text-xs sm:text-sm text-white mb-0.5 sm:mb-1 break-words">
                  {toast.title}
                </p>
                {toast.message && (
                  <p className="font-inter text-[10px] sm:text-xs text-slate-200 break-words">
                    {toast.message}
                  </p>
                )}
              </div>

              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 p-1 hover:bg-white/15 active:bg-white/15 rounded transition-all touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-white"
                aria-label="Close notification"
              >
                <X className="w-4 h-4 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
